import { motion } from 'framer-motion';
import { getOrientation } from 'get-orientation/browser';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { FiCamera, FiRotateCcw, FiRotateCw } from 'react-icons/fi';
import { useMutation } from 'react-query';

import icPeople from '@/assets/ic_people-2.svg';
import { Button, InputSlider, Loader, Modal } from '@/components';
import {
  getCroppedImg,
  getRotatedImage,
} from '@/components/ReactEasyCrop/CanvasUtils';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import {
  setPredefinedProfilePic,
  uploadProfilePic,
} from '@/services/minileagues';
import { ImageCover } from '@/store';
import { GDTApiErrors } from '@/types';
import { DatosFotoMLDTO, ResponseUploadFotoML } from '@/types/minileagues';
import { COLORS, FADE_IN_OUT, formatFileSize, THUMBS } from '@/utils';

const MAX_FILE_SIZE_KB = 2000;
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'] as const;

const ORIENTATION_TO_ANGLE = {
  '3': 180,
  '6': 90,
  '8': -90,
} as const;

const INITIAL_CROP = { x: 0, y: 0 };
const INITIAL_CROPPED_AREA: Area = {
  width: 0,
  height: 0,
  x: 0,
  y: 0,
};

function readFile(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener(
      'load',
      () => resolve(reader.result as string),
      false
    );

    reader.addEventListener(
      'error',
      () => reject(new Error('No se pudo leer el archivo')),
      false
    );

    reader.readAsDataURL(file);
  });
}

interface State extends DatosFotoMLDTO {
  showModalProfilePic: boolean;
}

const INIT_STATE: State = {
  showModalProfilePic: false,
};
export const MiniligueProfilePic: React.FC = React.memo(() => {
  const { tourneyData } = useTourney();
  const toast = useToast();
  const { isDesktop } = useIsDesktop();

  // Imagen temporal que se edita en el cropper
  const [imageSrc, setImageSrc] = useState('');
  const [crop, setCrop] = useState(INITIAL_CROP);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState<Area>(INITIAL_CROPPED_AREA);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profilePic, setProfilePic] = useState<State>(INIT_STATE);

  useEffect(() => {
    setProfilePic((prev) => ({ ...prev, ...tourneyData?.tourney?.fotoML }));
  }, [tourneyData.tourney?.fotoML]);

  const resetEditorState = useCallback(() => {
    setImageSrc('');
    setCrop(INITIAL_CROP);
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(INITIAL_CROPPED_AREA);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const onCropComplete = useCallback((_croppedArea: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const {
    isLoading,
    mutateAsync: requestSavePic,
    reset: resetMutation,
    isSuccess,
  } = useMutation(
    'saveProfilePicTda',
    async (image: Blob) => uploadProfilePic(image, tourneyData.id),
    {
      onSuccess: (response: ResponseUploadFotoML) => {
        if (!response.estado) {
          toast.show(
            'Algo salió mal mientras intentabas guardar la foto de perfil'
          );
          return;
        }

        // TODO: por si piden eliminar en algun momento portada
        // deleteCoverImage(idTda);
        setTimeout(() => {
          setProfilePic({
            ...profilePic,
            pathImagen: response.fotoML.pathImagen || undefined,
            idFotoPredefinida: response?.fotoML?.idFotoPredefinida || undefined,
            showModalProfilePic: false,
          });
        }, 300);

        resetEditorState();
      },
      onError: (err: GDTApiErrors) => {
        toast.show(err.mensaje);
      },
      onSettled: () => {
        resetMutation();
      },
    }
  );

  const savePic = useCallback(async () => {
    try {
      if (!imageSrc || isLoading) return;

      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, {
        rotation,
        outputType: 'image/jpeg',
        quality: 0.9,
      });

      if (!croppedImage) {
        toast.show('No se pudo generar la imagen recortada');
        return;
      }

      await requestSavePic(croppedImage);
    } catch (e) {
      console.error(e);
      toast.show('Hubo un problema al procesar la imagen');
    }
  }, [croppedAreaPixels, imageSrc, isLoading, requestSavePic, rotation, toast]);

  const onClose = useCallback(() => {
    resetMutation();
    resetEditorState();
  }, [resetEditorState, resetMutation]);

  const rotateLeft = useCallback(() => setRotation((prev) => prev - 90), []);
  const rotateRight = useCallback(() => setRotation((prev) => prev + 90), []);

  const cropSizeByDevices = !isDesktop
    ? { width: 333, height: 333 }
    : { width: 182, height: 182 };

  const validateFile = useCallback(
    (file: File) => {
      const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;

      if (
        !ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])
      ) {
        toast.show(
          "Sólo se permiten archivos de extensión '.jpg', '.jpeg', '.png' o '.gif'."
        );
        return false;
      }

      if (file.size > MAX_FILE_SIZE_KB * 1000) {
        toast.show(
          `El tamaño del archivo debe ser menor que ${formatFileSize(
            MAX_FILE_SIZE_KB * 1000
          )}. Por favor, ingresalo nuevamente.`
        );
        return false;
      }

      return true;
    },
    [toast]
  );

  const onFileChange = useCallback(
    async (target: HTMLInputElement | null | undefined) => {
      if (!target?.files?.length) return;

      try {
        const file = target.files[0];

        if (!validateFile(file)) return;

        let imageDataUrl = await readFile(file);

        try {
          const orientation = await getOrientation(file);
          const rotationFromExif =
            ORIENTATION_TO_ANGLE[
              String(orientation) as keyof typeof ORIENTATION_TO_ANGLE
            ];

          if (rotationFromExif) {
            imageDataUrl = await getRotatedImage(
              imageDataUrl,
              rotationFromExif
            );
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('failed to detect the orientation', e);
        }

        setCrop(INITIAL_CROP);
        setZoom(1);
        setRotation(0);
        setCroppedAreaPixels(INITIAL_CROPPED_AREA);
        setImageSrc(imageDataUrl);
      } finally {
        target.value = '';
      }
    },
    [validateFile]
  );

  // persistencia de portadas por defecto
  const onHandleThumbsSelected = async (image: ImageCover) => {
    try {
      // TODO: ENVIAR IMAGEN PREDEFINIDA
      const response = await setPredefinedProfilePic(tourneyData?.id, image.id);
      if (response.estado && response.fotoML?.idFotoPredefinida) {
        setProfilePic({
          ...profilePic,
          idFotoPredefinida:
            String(THUMBS.find((e) => e.id === image.id)?.id) || undefined,
          pathImagen: undefined,
          showModalProfilePic: false,
        });
      } else {
        console.error(response.mensaje);
        toast.show(response.mensaje);
      }
    } catch (error) {
      console.error(error, error);
    }
  };

  // Thumb predefinido seleccionado (idFotoPredefinida es string, los id de THUMBS son number)
  const selectedThumb = useMemo(
    () =>
      THUMBS.find(
        (item) => item.id === parseInt(profilePic?.idFotoPredefinida || '', 10)
      ),
    [profilePic?.idFotoPredefinida]
  );

  // Imagen a mostrar: la del thumb predefinido (si tiene) o la subida por el usuario
  const imageSource = selectedThumb?.src || profilePic?.pathImagen;

  // El thumb es de color cuando está seleccionado y no tiene imagen (THUMBS 7-9)
  const backgroundColor =
    selectedThumb && !selectedThumb.src ? selectedThumb.color : undefined;

  return (
    <>
      <Loader
        isVisible={(isLoading && !isSuccess) || tourneyData.tourney === null}
      />
      {/* IMAGEN DE PERFIL DE TDA */}
      <motion.div animate={'show'} initial="hide" variants={FADE_IN_OUT}>
        <div className="w-[133px] h-[133px] relative mx-4 bottom-6">
          <figure
            className="border-white w-[133px] h-[133px] border-2 bg-gdt-blue-xl2 rounded-full flex items-center justify-center overflow-hidden"
            style={{
              ...(backgroundColor ? { backgroundColor } : undefined),
              cursor: tourneyData.isOwner ? 'pointer' : undefined,
            }}
            onClick={() =>
              tourneyData.isOwner
                ? setProfilePic({
                    ...profilePic,
                    showModalProfilePic: true,
                  })
                : undefined
            }
          >
            {imageSource ? (
              <img
                alt="Foto de perfil"
                className="w-full h-full object-cover object-center"
                src={imageSource}
              />
            ) : (
              !backgroundColor && <img alt="" src={icPeople} />
            )}
          </figure>
          {tourneyData.isOwner && (
            <figure
              className="w-8 h-8 bg-gdt-blue rounded-full flex items-center justify-center absolute bottom-0 right-0 cursor-pointer overflow-hidden"
              onClick={() =>
                setProfilePic({
                  ...profilePic,
                  showModalProfilePic: true,
                })
              }
            >
              <FiCamera color="white" size={17} />
            </figure>
          )}
        </div>
      </motion.div>
      {/* END IMAGEN DE PERFIL DE TDA */}

      {/* MODAL SELECTOR */}
      <Modal
        showCloseButton
        className="!max-w-[449px] px-6"
        controlledOnClose={() =>
          setProfilePic({
            ...profilePic,
            showModalProfilePic: false,
          })
        }
        isControlledOpen={Boolean(profilePic.showModalProfilePic)}
        render={() => (
          <div className="text-center mt-9">
            <div className="grid grid-cols-3 gap-3">
              {THUMBS.map((img) => (
                <figure
                  key={img.id}
                  className="w-32 h-[89px] rounded-lg cursor-pointer"
                  style={{ backgroundColor: img.color }}
                  onClick={() => onHandleThumbsSelected(img)}
                >
                  {img.src && <img alt={img.src} src={img.src} />}
                </figure>
              ))}
            </div>
            <Button
              className="mt-9 relative w-52 h-[48px]"
              type="button"
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                accept=".jpg,.jpeg,.png,.gif"
                style={{ display: 'none' }}
                type="file"
                onChange={(e) => onFileChange(e.target)}
              />
              Cargar una imagen
            </Button>
          </div>
        )}
        size="xs"
        title="Elegí una foto de perfil"
        variant="controlled"
      />

      {/* MODAL EDITAR FOTO DE PERFIL */}
      <Modal
        bottomSheetClassName="max-md:px-0"
        className="pt-0 lg:px-6 px-0 pb-8 !max-w-[450px] !w-full"
        closeOutside={false}
        controlledOnClose={onClose}
        disableDrag={true}
        isControlledOpen={Boolean(imageSrc)}
        render={() => {
          return (
            <>
              <p className="font-bold text-base text-black text-center mt-0 mb-6 lg:my-6">
                Editar foto de perfil
              </p>
              <div className="relative w-full h-[360px] lg:h-[223px] bg-gdt-gray-7">
                <Cropper
                  aspect={1}
                  crop={crop}
                  cropShape="round"
                  cropSize={cropSizeByDevices}
                  image={imageSrc}
                  restrictPosition={false}
                  rotation={rotation}
                  showGrid={false}
                  zoom={zoom}
                  zoomSpeed={0.1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onRotationChange={setRotation}
                  onZoomChange={setZoom}
                />
              </div>

              <div className="mx-5 lg:mx-0">
                <div className="flex w-full justify-between items-center px-2 lg:px-0">
                  <div className="flex-1 items-center justify-between -mt-5 lg:ml-2">
                    <InputSlider
                      backgroundColor={COLORS['gdt-blue'].header}
                      foregroundColor={COLORS['gdt-gray'][5]}
                      maxInputRangeAmmount={3}
                      minInputRangeAmmount={1}
                      showThumb={false}
                      step={0.001}
                      values={[zoom]}
                      onChange={(e) => setZoom(Number(e.join()))}
                    />
                    <div className="flex justify-between w-[107%] -left-[3.5%] relative items-center text-xs text-gdt-gray-4 top-[-5px]" />
                  </div>

                  <div className="w-[45px] lg:w-[85px] flex justify-end lg:justify-center items-center space-x-4 pt-2">
                    {isDesktop && (
                      <button
                        aria-label="Rotar izquierda"
                        type="button"
                        onClick={rotateLeft}
                      >
                        <FiRotateCcw
                          color={COLORS['gdt-blue'].DEFAULT}
                          size={20}
                        />
                      </button>
                    )}
                    <button
                      aria-label="Rotar derecha"
                      type="button"
                      onClick={rotateRight}
                    >
                      <FiRotateCw
                        color={COLORS['gdt-blue'].DEFAULT}
                        size={20}
                      />
                    </button>
                  </div>
                </div>

                <span className="text-xs text-gdt-gray-4">Zoom</span>
                <div className="flex justify-around my-6 space-x-2">
                  <Button
                    aria-label="Cancelar guardado"
                    className="flex-1"
                    type="button"
                    variant="secondary"
                    onClick={() => onClose()}
                  >
                    Cancelar
                  </Button>

                  <Button
                    aria-label="Guardar foto de perfil"
                    className="flex-1"
                    disabled={isLoading}
                    type="button"
                    variant="primary"
                    onClick={savePic}
                  >
                    Guardar foto
                  </Button>
                </div>
              </div>
            </>
          );
        }}
        size="xs"
        variant="controlled"
      />
    </>
  );
});

MiniligueProfilePic.displayName = 'MiniligueProfilePic';
